import React from 'react';
import { motion } from 'framer-motion';
import {
  Plan,
  PlanSection,
  commissionParts,
  exampleParts,
  formatPlanPrice,
  isSalesLed,
  priceSuffix,
  setupFeeText,
} from '../lib/plans';

/**
 * Dashboard (dark) rendering of a plan page: intro, plan cards, list sections, closing call
 * to action. All text comes from subscription_plans / subscription_plan_sections; this file
 * only lays it out. Used by the merchant and the supplier plan screens.
 */

const Check: React.FC<{ strong?: boolean }> = ({ strong }) => (
  <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${strong ? 'text-orange-400' : 'text-white/40'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const buttonClass = (strong: boolean) =>
  `block text-center w-full py-3 rounded-full font-semibold text-sm transition-colors disabled:opacity-50 ${
    strong
      ? 'bg-orange-500 hover:bg-orange-400 text-black'
      : 'border border-white/25 hover:border-white/40 text-white hover:bg-white/5'
  }`;

export const PlanIntro: React.FC<{ section?: PlanSection; fallbackHeading: string; children?: React.ReactNode }> = ({
  section,
  fallbackHeading,
  children,
}) => (
  <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
    {section?.subheading && <p className="text-orange-400 text-xs font-semibold tracking-widest mb-3">{section.subheading}</p>}
    <h1 className="font-heading font-bold text-white text-3xl mb-3">{section?.heading || fallbackHeading}</h1>
    {section?.body && <p className="text-white/50 text-base max-w-2xl mx-auto mb-4">{section.body}</p>}
    {children}
  </motion.div>
);

type CardProps = {
  plan: Plan;
  index: number;
  busy: boolean;
  onCheckout?: (planId: string) => void;
};

export const PlanCard: React.FC<CardProps> = ({ plan, index, busy, onCheckout }) => {
  const strong = plan.highlight;
  const price = formatPlanPrice(plan);
  const commission = commissionParts(plan);
  const setup = setupFeeText(plan);
  const example = exampleParts(plan);

  return (
    <motion.div
      className={`relative ${strong ? 'glass-card-strong' : 'glass-card'} rounded-2xl p-6 overflow-hidden flex flex-col`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      {strong && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at top, rgba(249,115,22,0.15) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div className="relative flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <h3 className="font-heading font-bold text-white text-xl">{plan.name}</h3>
          {plan.tagline && <span className="text-orange-400 text-xs font-semibold tracking-widest">{plan.tagline}</span>}
        </div>
        {plan.description && <p className="text-white/50 text-sm mb-5">{plan.description}</p>}

        <ul className="space-y-1.5 mb-4">
          {price && (
            <li className="text-white/60 text-sm">
              <span className={`font-heading font-bold text-3xl mr-1.5 ${strong ? 'text-orange-400' : 'text-white'}`}>{price}</span>
              {priceSuffix(plan)}
            </li>
          )}
          {commission && (
            <li className="text-white/60 text-sm">
              <span className="font-semibold text-white">{commission[0]}</span> {commission[1]}
            </li>
          )}
          {setup && (
            <li className="text-white/60 text-sm">
              One-off setup: <span className="font-semibold text-white">{setup}</span>
            </li>
          )}
        </ul>

        {example && (
          <p className="text-white/40 text-xs mb-5">
            Example: {example[0]} in sales through FLOWSTOCK per month costs <span className="font-semibold text-white/70">{example[1]}</span>.
          </p>
        )}

        <ul className="space-y-2.5 mb-8 flex-1">
          {(plan.features || []).map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2.5">
              <Check strong={strong} />
              <span className="text-white/60 text-sm">{feature}</span>
            </li>
          ))}
        </ul>

        {isSalesLed(plan) || !onCheckout || price === null ? (
          <a href={plan.contact_url || 'mailto:hello@flowstock.so'} className={buttonClass(strong)}>
            {plan.cta_label || 'Contact us'}
          </a>
        ) : (
          <button onClick={() => onCheckout(plan.id)} disabled={busy} className={buttonClass(strong)}>
            {busy ? 'Processing...' : `Select ${plan.name}`}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const PlanListSections: React.FC<{ sections: PlanSection[] }> = ({ sections }) =>
  sections.length === 0 ? null : (
    <div className={`grid grid-cols-1 ${sections.length >= 3 ? 'md:grid-cols-3' : sections.length === 2 ? 'md:grid-cols-2' : ''} gap-6 mb-10`}>
      {sections.map((s) => (
        <div key={s.id} className="glass-card rounded-2xl p-6">
          <h4 className="font-heading font-semibold text-white text-sm tracking-widest mb-4">{s.heading}</h4>
          <ul className="space-y-2.5">
            {(s.items || []).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5">
                <Check />
                <span className="text-white/60 text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

export const PlanClosingCta: React.FC<{ section?: PlanSection }> = ({ section }) =>
  !section ? null : (
    <div className="text-center mt-4">
      <h4 className="font-heading font-bold text-white text-xl mb-4">{section.heading}</h4>
      {section.link_url && section.link_label && (
        <a href={section.link_url} className="inline-block bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm px-8 py-3 rounded-full transition-colors">
          {section.link_label}
        </a>
      )}
    </div>
  );

export const gridColsFor = (n: number): string =>
  n >= 3 ? 'md:grid-cols-3' : n === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto';
